import {isUndefined, keyBy} from 'lodash';
import React, {Fragment, useEffect} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncMoreStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {DecisionTreeModel} from '../../../../libs/shared/core/src/models/api/decisionTreeModel';
import {logger} from '../../../../libs/shared/core/src/services/logger/logger';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipExplicitCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipExplicitCondition';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {MultiSelectFilterDropdown} from '../../../components/common/filters/multiSelectFilter/multiSelectFilterDropdown';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {useFetchDecisionTrees} from '../../../settings/fetchers/decisionTrees';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.CHATBOTS];

/*
 * Styles.
 */

const StyledTooltipWrapperDiv = styled.div`
  display: inline-flex;
  align-items: center;
  line-height: ${fontSizes.veryLarge};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

/*
 * Hook.
 */

export const useAnalyticsChatbotsFilter = (): AnalyticsFilterItem => {
  const [selectedChatbotIds, onUpdate] = useAnalyticsFilter(AnalyticsFilterTypesEnum.CHATBOTS);
  const {trackOnEdit} = useFiltersTracking(AnalyticsFilterTypesEnum.CHATBOTS);
  const isActive = !isUndefined(selectedChatbotIds) && selectedChatbotIds?.length > 0;

  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const {items: allChatbots, moreStatus} = useFetchDecisionTrees(namespace);
  const hasChatbots = Boolean(allChatbots.length);
  const isDisabled = !hasChatbots;
  const theme = useUserTheme();

  // If user has more than 50 decision trees, which is the current limit for pagination
  if (moreStatus === AsyncMoreStatusesEnum.HAS_MORE) {
    logger.warn('Partial chatbot resource fetch');
  }

  const chatbotsMap = keyBy(allChatbots, (chatbot) => chatbot.id);

  const onSelectItems = (updatedSelectedItems: ReadonlyArray<number>) => {
    onUpdate(updatedSelectedItems);
    trackOnEdit(updatedSelectedItems, selectedChatbotIds);
  };

  // After loading all chatbots, if some exist, automatically select the most recently updated.
  useEffect(() => {
    if (hasChatbots) {
      const sortedChatbots = [...allChatbots].sort(
        (chatbotA, chatbotB) => chatbotB.updatedAt - chatbotA.updatedAt,
      );
      const mostRecentChatbot = sortedChatbots[0];
      onSelectItems([mostRecentChatbot.id]);
    }
  }, [allChatbots]);

  const renderButton = () => (
    <TooltipCoordinator placement="top" maxWidth={244} customMargin={8} render={renderTooltipContent}>
      <TooltipExplicitCondition isEnabled={isDisabled} />
      <AnalyticsFilterButtonValue
        selectedIds={selectedChatbotIds}
        itemsMap={chatbotsMap}
        renderItem={renderButtonItem}
        iconName={isActive ? activeIconName : iconName}
        iconColor={isDisabled ? theme.greys.shade50 : undefined}
        label={name}
        isActive={isActive}
        shouldLabelSelection={false}
      />
    </TooltipCoordinator>
  );

  const renderDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <MultiSelectFilterDropdown
      {...dropdownProps}
      title={name}
      rows={allChatbots.map((chatbot) => ({
        renderer: () => <DropdownItemContent>{chatbot.name}</DropdownItemContent>,
        isSelected: selectedChatbotIds?.includes(chatbot.id) ?? false,
        onSelect: () => onSelectItems([chatbot.id]),
      }))}
      showClearButton={false}
      onClear={() => {}}
      isMultiSelect={false}
    />
  );

  return {
    id: 'chatbots-filter',
    type: AnalyticsFilterTypesEnum.CHATBOTS,
    isActive,
    isDisabled: !hasChatbots,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

const renderButtonItem = (chatbot: DecisionTreeModel) => <Fragment key={chatbot.id}>{chatbot.name}</Fragment>;

function renderTooltipContent() {
  return (
    <StyledTooltipWrapperDiv>
      <Trans id="analytics-automation-reports-chatbots-chatbots-filter-tooltip-content">
        This workspace has no chatbots.
      </Trans>
    </StyledTooltipWrapperDiv>
  );
}
